import React, { useMemo } from 'react';
import { isUseHelpCenterRefresh } from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import type { UserRequestsModel } from 'state/persisted/user';
import type { UserRequestsCounts } from 'state/persisted/user-requests-counts';
import { DumbHeader } from 'view/layout/header-dumb';
import type { DumbProps as HeaderDumpProps } from 'view/layout/header-dumb';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import type { layoutHeaderFragment$key } from './__generated__/layoutHeaderFragment.graphql';

export interface LayoutHeaderProps {
	firstSectionFragment: layoutHeaderFragment$key;
	hasTopBarBeenSplit: boolean;
	headerProps: {
		isUserLoaded?: boolean;
		userLoggedIn?: boolean | undefined;
		loginUrl?: string;
		isCustomizing?: boolean;
		userRequest: UserRequestsModel | UserRequestsCounts; //TODO: remove UserRequestsModel when removing isSplitRequestCountsFromUserModelEnabled
		organisations: HeaderDumpProps['organisations'];
		anonymousEnabled?: boolean;
		headerBackgroundColor?: string;
		logoUrl?: string;
		name?: string;
	};
}

// TODO: this is a temporary solution for keeping a transparent header, eventually we will remove this and use header from PaperLayout
export const LayoutHeader: React.FC<LayoutHeaderProps> = (props) => {
	di(useIsPageInEditMode, isUseHelpCenterRefresh);
	const data = useFragment(
		graphql`
			fragment layoutHeaderFragment on HelpLayoutSection {
				subsections {
					elements {
						__typename
					}
				}
				visualConfig {
					hidden
				}
			}
		`,
		props.firstSectionFragment,
	);
	const { hasTopBarBeenSplit } = props;

	const [isPageInEditMode] = useIsPageInEditMode();

	const isFirstSectionHero =
		data?.subsections?.[0]?.elements?.[0]?.__typename === 'HelpLayoutHeroElement';
	const isFirstSectionHidden = Boolean(data?.visualConfig?.hidden);

	const transparentHeader = useMemo(() => {
		if (isUseHelpCenterRefresh() && hasTopBarBeenSplit) {
			return false;
		}
		if (isPageInEditMode) {
			return isFirstSectionHero && !isFirstSectionHidden;
		}
		// view HomePage
		return isFirstSectionHero;
	}, [hasTopBarBeenSplit, isFirstSectionHero, isFirstSectionHidden, isPageInEditMode]);

	return (
		<Box xcss={transparentHeader ? absoluteHeader : relativeHeader}>
			<DumbHeader
				{...props.headerProps}
				isAnonymous={false}
				logoTextColor="banner"
				hideSearchButton
				showThemedBackground={!transparentHeader}
				disableAllHeaderLinks={isPageInEditMode}
			/>
		</Box>
	);
};

const absoluteHeader = xcss({
	position: 'absolute',
	top: 'space.0',
	width: '100%',
	zIndex: 'navigation',
});

const relativeHeader = xcss({
	position: 'relative',
	width: '100%',
});
